import { faFilter } from "@fortawesome/pro-regular-svg-icons";
import { faFilter as faFilterSolid } from "@fortawesome/pro-solid-svg-icons";
import { CloseButton } from "@headlessui/react";
import { endOfDay, startOfDay } from "date-fns";
import { SelectRangeEventHandler } from "react-day-picker";

import { Button } from "src/base-components/Button";
import { DateRangePicker } from "src/base-components/DateRangePicker";
import { Icon } from "src/base-components/Icon";
import {
  URLParamFilter,
  useFilter,
} from "src/base-components/tableColumnFilters/useFilters";

const getDateFromFilters = (filter: URLParamFilter | undefined) => {
  if (!filter) return undefined;

  const date = new Date(filter.values[0]);
  return isNaN(date.getTime()) ? undefined : date;
};

export const DateRangeFilter: React.FC<{
  property: string;
  dataLoc: string;
}> = ({ property, dataLoc }) => {
  const { filters, update, apply, reset, isFiltering, isApplied } = useFilter(
    property,
    [
      { key: property, operator: "gte", values: [] },
      { key: property, operator: "lte", values: [] },
    ],
  );

  const handleChange: SelectRangeEventHandler = (value) => {
    if (value?.from || value?.to) {
      const from = startOfDay((value.from ?? value.to) as Date);
      const to = endOfDay((value.to ?? value.from) as Date);

      update([
        {
          key: property,
          operator: "gte",
          values: [from.toISOString()],
        },
        {
          key: property,
          operator: "lte",
          values: [to.toISOString()],
        },
      ]);
    }
  };

  const isFilteringProperty = isFiltering({ key: property });

  return (
    <span>
      <DateRangePicker
        buttonRenderer={({ open }) => (
          <Icon
            color={
              open || isFilteringProperty
                ? "text-indigo-500"
                : "text-gray-500 hover:text-gray-700"
            }
            cursorType="pointer"
            dataLoc={dataLoc}
            icon={open || isFilteringProperty ? faFilterSolid : faFilter}
            size="2xs"
          />
        )}
        footerRenderer={() => (
          <div className="mt-2 flex justify-between border-gray-200 border-t p-4 pb-2">
            <CloseButton
              as={Button}
              dataLoc={`${dataLoc}-reset-button`}
              disabled={!isFilteringProperty}
              size="sm"
              variant="secondary"
              onClick={() => reset()}
            >
              Reset
            </CloseButton>
            <CloseButton
              as={Button}
              dataLoc={`${dataLoc}-apply-button`}
              disabled={isApplied()}
              size="sm"
              variant="primary"
              onClick={apply}
            >
              Apply
            </CloseButton>
          </div>
        )}
        value={{
          from: getDateFromFilters(filters.at(0)),
          to: getDateFromFilters(filters.at(1)),
        }}
        resetable
        usePredefinedDates
        onChange={handleChange}
        onReset={reset}
      />
    </span>
  );
};
